import {
  BoxGeometry,
  CylinderGeometry,
  Group,
  IcosahedronGeometry,
  Mesh,
  MeshLambertMaterial,
  SphereGeometry,
  type BufferGeometry,
  type Material,
} from 'three';

/**
 * EN-04 Sicario en parapente (GDD §8.2, issue #86): a caricature in low-poly primitives, with a visible canopy
 * that is paler than the rider so that it reads as harmless (GDD §14.1). No weapons are drawn and nobody is
 * hurt (GDD §3.4). The rider is about 1.1 x 1.3 u and its hitbox 0.8 x 1.0 u (70-80 %); the canopy is not part
 * of the hitbox.
 */
export interface Paraglider {
  group: Group;
  rider: Group;
  canopy: Mesh;
  dispose(): void;
}

export const PARAGLIDER_COLORS = { suit: 0x3b5a49, skin: 0xd9a77b, hat: 0xc9b089, canopy: 0xc7b8d8, rope: 0x8f8a80 } as const;

export function buildParaglider(): Paraglider {
  const geometries: BufferGeometry[] = [];
  const materials: Material[] = [];
  const paint = (color: number) => {
    const material = new MeshLambertMaterial({ color, flatShading: true });
    materials.push(material);
    return material;
  };
  const own = <T extends BufferGeometry>(geometry: T): T => {
    geometries.push(geometry);
    return geometry;
  };

  const group = new Group();
  const rider = new Group();
  rider.name = 'enemy-en04-rider';
  // Torso, head and hat, legs hanging and arms up to the lines: about 1.1 wide with the sombrero, 1.3 high.
  const torso = new Mesh(own(new BoxGeometry(0.5, 0.55, 0.3)), paint(PARAGLIDER_COLORS.suit));
  torso.position.set(0, 0.05, 0);
  const head = new Mesh(own(new IcosahedronGeometry(0.17, 0)), paint(PARAGLIDER_COLORS.skin));
  head.position.set(0, 0.42, 0);
  const brim = new Mesh(own(new CylinderGeometry(0.52, 0.52, 0.04, 8)), paint(PARAGLIDER_COLORS.hat));
  brim.position.set(0, 0.56, 0);
  const crown = new Mesh(own(new CylinderGeometry(0.2, 0.22, 0.2, 8)), paint(PARAGLIDER_COLORS.hat));
  crown.position.set(0, 0.66, 0);
  const legs = new Mesh(own(new BoxGeometry(0.4, 0.3, 0.25)), paint(PARAGLIDER_COLORS.suit));
  legs.position.set(0, -0.4, 0);
  const arms = new Mesh(own(new BoxGeometry(0.9, 0.1, 0.12)), paint(PARAGLIDER_COLORS.suit));
  arms.position.set(0, 0.35, 0);
  rider.add(torso, head, brim, crown, legs, arms);
  group.add(rider);

  // The canopy hangs above on two lines; it is a flattened half sphere, paler than the rider.
  const canopy = new Mesh(
    own(new SphereGeometry(1.1, 10, 4, 0, Math.PI * 2, 0, Math.PI / 2)),
    paint(PARAGLIDER_COLORS.canopy),
  );
  canopy.name = 'enemy-en04-canopy';
  canopy.scale.set(1, 0.45, 0.6);
  canopy.position.set(0, 1.7, 0);
  group.add(canopy);
  const rope = paint(PARAGLIDER_COLORS.rope);
  for (const x of [-0.35, 0.35]) {
    const line = new Mesh(own(new CylinderGeometry(0.012, 0.012, 1.05, 3)), rope);
    line.position.set(x, 1.1, 0);
    line.rotation.z = x > 0 ? 0.28 : -0.28;
    group.add(line);
  }

  return {
    group,
    rider,
    canopy,
    dispose() {
      for (const geometry of geometries) geometry.dispose();
      for (const material of materials) material.dispose();
    },
  };
}
